import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  styled,
  IconButton,
  Menu,
  MenuItem,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  ThemeProvider,
} from '@mui/material';
import { Search as SearchIcon, Sort as SortIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import gql from 'graphql-tag';
import moment from 'moment';
import Fuse from 'fuse.js';
import { debounce } from 'lodash';

import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import DisplayMedia from '../../../components/DisplayMedia';

const Container = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: theme.custom.background1,
  minHeight: '100vh',
}));

const HeaderContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '24px',
  gap: '12px',
});

const HeaderLink = styled(Typography)(({ theme }) => ({
  color: theme.custom.link,
  cursor: 'pointer',
  fontSize: '14px',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const ChevronRight = styled(Typography)(({ theme }) => ({
  color: theme.custom.textLight,
  fontSize: '14px',
}));

const SearchInput = styled('input')`
  padding: 8px 12px 8px 48px;
  border: 1px solid ${({ theme }) => theme.custom.stroke};
  border-radius: 8px;
  font-size: 14px;
  width: 100%;
  outline: none;
  height: 48px;
  background-color: ${({ theme }) => theme.custom.background2};
  color: ${({ theme }) => theme.custom.text};

  &:focus {
    border-color: ${({ theme }) => theme.custom.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.custom.textLight};
  }
`;

const SearchWrapper = styled(Box)`
  position: relative;

  & svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.custom.textLight};
  }
`;

const FiltersContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  padding: '0 4px',
});

const RewardCount = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: theme.custom.text,
}));

const SortButton = styled(IconButton)(({ theme }) => ({
  color: theme.custom.primary,
}));

const RewardListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  borderRadius: '12px',
  marginBottom: '8px',
  padding: '16px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const RewardAvatar = styled(Avatar)(({ theme }) => ({
  width: 43,
  height: 43,
  backgroundColor: theme.custom.background3,
  border: `1px solid ${theme.custom.stroke}`,
}));

const RewardContent = styled(Box)({
  flex: 1,
  marginLeft: '12px',
});

const RewardName = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: theme.custom.text,
  marginBottom: '4px',
}));

const RewardDate = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.custom.textLight,
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '300px',
});

const CustomMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.custom.background2,
    border: `1px solid ${theme.custom.stroke}`,
    borderRadius: '8px',
    boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.15)',
  },
  '& .MuiMenuItem-root': {
    color: theme.custom.text,
    fontSize: '14px',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: theme.custom.background3,
    },
    '&.Mui-selected': {
      backgroundColor: theme.custom.primary,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.custom.primary,
      },
    },
  },
}));

interface Reward {
  id: string;
  name: string;
  imageName: string;
  imageType: string;
  validFrom: string;
  validTo: string;
  user?: {
    name: string;
  };
  isDeleted: boolean;
}

const RewardsArchiveView: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [rewards, setRewards] = useState<Reward[]>([]);
  const [filteredRewards, setFilteredRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const fuseOptions = {
    keys: ['name'],
    includeScore: true,
    threshold: 0.6,
  };

  const fetchArchivedRewards = useCallback(async () => {
    try {
      setLoading(true);

      // const orgMemberQueryRes: any = await API.graphql({
      //   query: orgMemberQuery,
      //   variables: {
      //     userID: global.me.id,
      //     orgID: global.me.orgID,
      //     limit: 100000,
      //   },
      // });

      const archivedRewardsQuery = gql`
        query ListArchivedRewards(
          $orgID: ID!
          $today: String!
          $sortDirection: ModelSortDirection
          $nextToken: String
        ) {
          listRewardsByOrgName(
            orgID: $orgID
            filter: { isDeleted: { eq: false }, validTo: { lt: $today } }
            sortDirection: $sortDirection
            nextToken: $nextToken
          ) {
            items {
              id
              name
              imageName
              imageType
              validFrom
              validTo
              isDeleted
              createdBy
              orgID
              description
              rewardType
              value
              valueType
              cost
              costType
              remainingCount
              personLimit
              limitCount
            }
            nextToken
          }
        }
      `;

      const allRewards: Reward[] = [];
      let nextToken: string | null = null;

      do {
        const listArchivedRewardsRes: any = await API.graphql({
          query: archivedRewardsQuery as any,
          variables: {
            orgID: (global as any).me.orgID,
            today: new Date().toISOString().split('T')[0],
            sortDirection,
            nextToken,
          },
        });

        const rewards = listArchivedRewardsRes.data.listRewardsByOrgName.items;

        for (const reward of rewards) {
          if (!reward.isDeleted) {
            allRewards.push({
              ...reward,
              user: { name: '' },
            });
          }
        }

        nextToken = listArchivedRewardsRes.data.listRewardsByOrgName.nextToken;
      } while (nextToken !== null);

      setRewards(allRewards);
      setFilteredRewards(allRewards);
    } catch (error) {
      console.error('Error fetching archived rewards:', error);
    } finally {
      setLoading(false);
    }
  }, [sortDirection]);

  useEffect(() => {
    fetchArchivedRewards();
  }, [fetchArchivedRewards]);

  const handleSearch = useCallback(
    debounce((text: string) => {
      setSearchText(text);

      if (!text.trim()) {
        setFilteredRewards(rewards);
        return;
      }

      const fuse = new Fuse(rewards, fuseOptions);
      const searchResults = fuse.search(text);
      const formattedResults = searchResults.map((result) => result.item);
      setFilteredRewards(formattedResults);
    }, 300),
    [rewards]
  );

  const handleSortChange = (direction: 'ASC' | 'DESC') => {
    setSortDirection(direction);
    setSortAnchorEl(null);
  };

  const handleRewardClick = (reward: Reward) => {
    navigate('/performance/rewards/details', {
      state: {
        ...reward,
        returnTo: '/performance/rewards/archive',
      },
    });
  };

  const renderHeader = () => (
    <HeaderContainer>
      <HeaderLink onClick={() => navigate('/performance')}>
        Performance
      </HeaderLink>
      <ChevronRight>/</ChevronRight>
      <HeaderLink onClick={() => navigate('/performance/rewards')}>
        Rewards
      </HeaderLink>
      <ChevronRight>/</ChevronRight>
      <HeaderLink
        sx={{ cursor: 'default', '&:hover': { textDecoration: 'none' } }}
      >
        Archive
      </HeaderLink>
    </HeaderContainer>
  );

  const renderSearch = () => (
    <Box sx={{ marginBottom: '16px' }}>
      <SearchWrapper>
        <SearchIcon fontSize="small" />
        <SearchInput
          placeholder="Search"
          onChange={(e) => handleSearch(e.target.value)}
        />
      </SearchWrapper>
    </Box>
  );

  const renderFilters = () => (
    <FiltersContainer>
      <RewardCount>
        All ({searchText ? filteredRewards.length : rewards.length})
      </RewardCount>
      <SortButton
        onClick={(e) => setSortAnchorEl(e.currentTarget)}
        size="small"
      >
        <SortIcon />
      </SortButton>
      <CustomMenu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
      >
        <MenuItem
          onClick={() => handleSortChange('ASC')}
          selected={sortDirection === 'ASC'}
        >
          From A-Z
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange('DESC')}
          selected={sortDirection === 'DESC'}
        >
          From Z-A
        </MenuItem>
      </CustomMenu>
    </FiltersContainer>
  );

  const renderRewardItem = (reward: Reward) => (
    <RewardListItem key={reward.id} onClick={() => handleRewardClick(reward)}>
      <RewardAvatar>
        {reward.imageName ? (
          <DisplayMedia
            imageName={reward.imageName}
            imageType={reward.imageType}
            width="100%"
            height="100%"
            objectFit="contain"
            noWrapper
          />
        ) : (
          <Typography variant="caption" sx={{ color: theme.textLight }}>
            No Image
          </Typography>
        )}
      </RewardAvatar>

      <RewardContent>
        <RewardName>{reward.name}</RewardName>
        <RewardDate>
          {moment(reward.validFrom).format('MM/DD/YY')}
          {reward.user?.name && ` by ${reward.user.name}`}
        </RewardDate>
      </RewardContent>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}></Box>
    </RewardListItem>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      );
    }

    if (filteredRewards.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            color: theme.textLight,
          }}
        >
          <Typography>
            {searchText ? 'No rewards found' : 'No archived rewards'}
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ padding: 0 }}>{filteredRewards.map(renderRewardItem)}</List>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
        {renderHeader()}
        {renderSearch()}
        {renderFilters()}
        {renderContent()}
      </Container>
    </ThemeProvider>
  );
};

export default RewardsArchiveView;
